import { useEffect, useState } from "react";
import { Plus, Minus } from "lucide-react";

const FAQ_USERS = [
  { q: "Qu'est-ce que Lokaso ?", a: "Lokaso est l'annuaire de référence des entreprises de Guinée. Plus de 10 000 entreprises à Conakry et au-delà sont répertoriées sur lokasos.com — restaurants, pharmacies, hôtels, banques, transport et bien plus. Trouvez, contactez et évaluez les entreprises près de chez vous, gratuitement et sans inscription." },
  { q: "Comment trouver une entreprise sur Lokaso ?", a: "Rendez-vous sur lokasos.com et utilisez la barre de recherche pour taper le nom d'une entreprise ou d'une catégorie (ex: « pharmacie », « hôtel »). Vous pouvez aussi cliquer directement sur une catégorie depuis la page d'accueil. Les résultats affichent les entreprises correspondantes avec leurs coordonnées." },
  { q: "L'utilisation de Lokaso est-elle gratuite ?", a: "Oui, totalement. Rechercher une entreprise, consulter une fiche, contacter via WhatsApp, laisser un avis — tout est gratuit pour les utilisateurs. Sans inscription requise." },
  { q: "Comment contacter une entreprise depuis Lokaso ?", a: "Sur chaque fiche d'entreprise, vous trouverez des boutons d'action directs : Appeler, WhatsApp, Voir sur la carte. Un clic sur WhatsApp ouvre directement une conversation avec l'entreprise." },
  { q: "Comment installer Lokaso sur mon téléphone ?", a: "Sur Android (Chrome) : ouvrez lokasos.com → appuyez sur les 3 points ⋮ → « Ajouter à l'écran d'accueil ». Sur iPhone (Safari) : ouvrez lokasos.com → appuyez sur le bouton Partager → « Sur l'écran d'accueil ». L'application est installée instantanément, gratuitement, sans passer par un store. Ou plus simple : rendez-vous sur <a href=\"/app\" data-testid=\"faq-install-link\">lokasos.com/app</a> et laissez-vous guider." },
  { q: "Comment laisser un avis sur une entreprise ?", a: "Ouvrez la fiche de l'entreprise sur lokasos.com, faites défiler jusqu'à la section « Avis », cliquez sur « Laisser un avis » et remplissez le formulaire. Un compte Lokaso gratuit est nécessaire pour publier un avis." },
  { q: "Mes données personnelles sont-elles protégées ?", a: "Oui. Lokaso ne vend jamais vos données personnelles. Nous utilisons Google Analytics avec IP anonymisée pour analyser l'audience. Vous pouvez consulter notre Politique de Confidentialité complète en bas de page pour tous les détails." },
  { q: "Lokaso est-il disponible partout en Guinée ?", a: "Lokaso couvre actuellement principalement Conakry et ses communes (Kaloum, Dixinn, Ratoma, Matam, Matoto). Nous étendons progressivement notre couverture à l'intérieur du pays. Si votre entreprise est en dehors de Conakry, vous pouvez quand même l'inscrire." },
];

const FAQ_BIZ = [
  { q: "Mon entreprise est déjà sur Lokaso — comment est-ce possible ?", a: "Lokaso a constitué sa base de données initiale en important des informations d'entreprises depuis des sources publiques disponibles en Guinée. Si votre entreprise y figure, ses informations de base (nom, adresse, téléphone, catégorie) sont affichées. Vous pouvez réclamer gratuitement votre fiche pour la mettre à jour et la personnaliser." },
  { q: "Comment réclamer ma fiche gratuitement ?", a: "Cherchez votre entreprise sur lokasos.com → ouvrez sa fiche → cliquez sur « Réclamer cette fiche » → remplissez le formulaire de vérification. Notre équipe valide votre demande sous 48 heures. Une fois réclamée, vous pouvez modifier toutes les informations de votre fiche." },
  { q: "Que puis-je modifier sur ma fiche une fois réclamée ?", a: "Tout : nom, adresse, description, horaires d'ouverture, numéro de téléphone, numéro WhatsApp, photos, catégorie, prix indicatif. Une fiche complète génère significativement plus de contacts clients." },
  { q: "Qu'est-ce que le plan « Mis en avant » ?", a: "Le plan Mis en avant place votre entreprise en tête des résultats de recherche dans votre catégorie, avec une mise en avant visuelle distincte. Vos clients potentiels vous voient en premier." },
  { q: "Combien coûte le plan Mis en avant ?", a: "Le plan Mis en avant est disponible à 100 000 GNF par mois. Le plan de base (fiche standard) reste entièrement gratuit." },
  { q: "Comment apparaître en tête des résultats de recherche ?", a: "Deux leviers : (1) réclamez et complétez entièrement votre fiche — les fiches complètes sont mieux classées, (2) souscrivez au plan Mis en avant à 100 000 GNF/mois pour un positionnement prioritaire garanti." },
  { q: "Mon entreprise n'est pas encore sur Lokaso — comment l'inscrire ?", a: "Cliquez sur « Inscrire mon entreprise » dans le menu de navigation ou rendez-vous directement sur lokasos.com/register. L'inscription est gratuite et prend moins de 5 minutes." },
  { q: "Comment supprimer ma fiche de Lokaso ?", a: "Envoyez une demande par email à admin@lokasos.com avec le nom de votre entreprise et votre lien avec elle. Nous traitons toute demande de suppression dans un délai de 15 jours ouvrables." },
  { q: "Comment contacter l'équipe Lokaso ?", a: "Email : admin@lokasos.com | WhatsApp : +1 240-285-9605 | Nous répondons personnellement à chaque message." },
];

function injectFaqSchema(items) {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a.replace(/<[^>]*>/g, "") },
    })),
  };
  let s = document.getElementById("faq-jsonld");
  if (!s) {
    s = document.createElement("script");
    s.id = "faq-jsonld";
    s.type = "application/ld+json";
    document.head.appendChild(s);
  }
  s.textContent = JSON.stringify(data);
}

function Accordion({ items, openIdx, setOpenIdx, prefix }) {
  return (
    <div className="space-y-3" data-testid={`faq-accordion-${prefix}`}>
      {items.map((it, i) => {
        const open = openIdx === i;
        return (
          <div key={i} className={`rounded-xl border-2 overflow-hidden transition-colors ${open ? "border-lokaso-gold bg-lokaso-cream" : "border-stone-200 bg-white"}`}>
            <button
              type="button"
              onClick={() => setOpenIdx(open ? -1 : i)}
              className={`w-full flex items-center justify-between gap-3 px-5 py-4 text-left font-semibold transition-colors ${open ? "text-lokaso-green" : "text-lokaso-dark hover:bg-stone-50"}`}
              aria-expanded={open}
              data-testid={`faq-q-${prefix}-${i}`}
            >
              <span className="flex-1">{it.q}</span>
              <span className="shrink-0 w-7 h-7 rounded-full bg-lokaso-gold/20 text-lokaso-gold flex items-center justify-center">
                {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </span>
            </button>
            <div
              className="grid transition-all duration-300 ease-in-out"
              style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
            >
              <div className="overflow-hidden">
                <div
                  className="px-5 pb-5 text-stone-700 leading-relaxed whitespace-pre-line [&_a]:text-lokaso-green [&_a]:underline [&_a]:font-semibold hover:[&_a]:text-lokaso-gold"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: it.a }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function FAQPage() {
  const [tab, setTab] = useState("users");
  const [openIdx, setOpenIdx] = useState(-1);

  useEffect(() => {
    document.title = "FAQ · Lokaso";
    injectFaqSchema([...FAQ_USERS, ...FAQ_BIZ]);
    return () => {
      const s = document.getElementById("faq-jsonld");
      if (s) s.remove();
    };
  }, []);

  // Reset open question when switching tabs
  useEffect(() => { setOpenIdx(-1); }, [tab]);

  return (
    <div className="bg-lokaso-sand min-h-screen">
      <div className="bg-lokaso-green text-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16 text-center">
          <h1 className="font-display text-4xl md:text-6xl font-bold">Questions Fréquentes</h1>
          <p className="mt-3 text-lokaso-gold text-base md:text-lg">Tout ce que vous devez savoir sur Lokaso</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-10">
        {/* Tabs */}
        <div className="flex gap-2 border-b border-stone-200 mb-6" data-testid="faq-tabs">
          <button
            type="button"
            onClick={() => setTab("users")}
            className={`px-5 py-3 text-sm md:text-base font-semibold border-b-2 -mb-px ${tab === "users" ? "border-lokaso-green text-lokaso-green" : "border-transparent text-stone-500 hover:text-lokaso-dark"}`}
            data-testid="faq-tab-users"
          >
            Utilisateurs
          </button>
          <button
            type="button"
            onClick={() => setTab("biz")}
            className={`px-5 py-3 text-sm md:text-base font-semibold border-b-2 -mb-px ${tab === "biz" ? "border-lokaso-green text-lokaso-green" : "border-transparent text-stone-500 hover:text-lokaso-dark"}`}
            data-testid="faq-tab-biz"
          >
            Entreprises
          </button>
        </div>

        <Accordion
          items={tab === "users" ? FAQ_USERS : FAQ_BIZ}
          openIdx={openIdx}
          setOpenIdx={setOpenIdx}
          prefix={tab}
        />

        <p className="mt-12 text-center text-sm text-stone-500">
          Une question sans réponse ? Écrivez-nous à <a href="mailto:admin@lokasos.com" className="text-lokaso-green font-semibold">admin@lokasos.com</a>
        </p>
      </div>
    </div>
  );
}
